import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Alert,
  FormHelperText,
  Divider,
  Typography,
} from '@mui/material';
import { RestartAlt, Warning } from '@mui/icons-material';
import { Portfolio } from '../types';
import { PortfolioService } from '../services/portfolioService';

interface EditPortfolioDialogProps {
  open: boolean;
  onClose: () => void;
  portfolio: Portfolio | null;
  onPortfolioUpdated: () => void;
}

const EditPortfolioDialog: React.FC<EditPortfolioDialogProps> = ({
  open,
  onClose,
  portfolio,
  onPortfolioUpdated,
}) => {
  const [name, setName] = useState('');
  const [maxPositions, setMaxPositions] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showResetConfirm, setShowResetConfirm] = useState(false);
  const [resetLoading, setResetLoading] = useState(false);

  useEffect(() => {
    if (portfolio) {
      setName(portfolio.name);
      setMaxPositions(portfolio.maxPositions.toString());
    }
  }, [portfolio]);

  const handleClose = () => {
    setName('');
    setMaxPositions('');
    setError(null);
    setShowResetConfirm(false);
    onClose();
  };

  const handleReset = async () => {
    if (!portfolio) return;

    setError(null);
    setResetLoading(true);

    try {
      await PortfolioService.resetPortfolio(portfolio.id);
      onPortfolioUpdated();
      handleClose();
    } catch (err: any) {
      setError(err.message || 'Failed to reset portfolio');
      setShowResetConfirm(false);
    } finally {
      setResetLoading(false);
    }
  };

  const handleSave = async () => {
    if (!portfolio) return;

    setError(null);
    setLoading(true);

    try {
      const maxPos = parseInt(maxPositions);

      // Validation
      if (!name.trim()) {
        throw new Error('Portfolio name is required');
      }

      if (isNaN(maxPos) || maxPos < 1 || maxPos > 20) {
        throw new Error('Position capacity must be between 1 and 20');
      }

      // Update if changed
      let updated = false;

      if (name.trim() !== portfolio.name) {
        await PortfolioService.updatePortfolioName(portfolio.id, name.trim());
        updated = true;
      }

      if (maxPos !== portfolio.maxPositions) {
        await PortfolioService.updatePortfolioCapacity(portfolio.id, maxPos);
        updated = true;
      }

      if (updated) {
        onPortfolioUpdated();
      }

      handleClose();
    } catch (err: any) {
      setError(err.message || 'Failed to update portfolio');
    } finally {
      setLoading(false);
    }
  };

  if (!portfolio) return null;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Edit Portfolio Settings</DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 2 }}>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <TextField
            fullWidth
            label="Portfolio Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            sx={{ mb: 3 }}
            autoFocus
          />

          <TextField
            fullWidth
            label="Position Capacity (Tranches)"
            type="number"
            value={maxPositions}
            onChange={(e) => setMaxPositions(e.target.value)}
            inputProps={{ min: 1, max: 20 }}
            helperText="Number of positions this portfolio can hold simultaneously"
          />

          <FormHelperText sx={{ mt: 2 }}>
            Current positions: {portfolio.positionSize || 0} / {portfolio.maxPositions}
          </FormHelperText>

          {/* Reset Portfolio Section - Only for Virtual Portfolios */}
          {portfolio.type === 'virtual' && (
            <>
              <Divider sx={{ my: 3 }} />
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Reset Portfolio
              </Typography>
              {!showResetConfirm ? (
                <Box>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Reset this virtual portfolio to start fresh. This will delete all positions,
                    transactions, and recommendations, returning available funds to the initial value.
                  </Typography>
                  <Button
                    variant="outlined"
                    color="warning"
                    startIcon={<RestartAlt />}
                    onClick={() => setShowResetConfirm(true)}
                  >
                    Reset Portfolio
                  </Button>
                </Box>
              ) : (
                <Alert
                  severity="warning"
                  icon={<Warning />}
                  sx={{ mb: 2 }}
                  action={
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Button
                        color="inherit"
                        size="small"
                        onClick={() => setShowResetConfirm(false)}
                        disabled={resetLoading}
                      >
                        Cancel
                      </Button>
                      <Button
                        color="warning"
                        size="small"
                        variant="contained"
                        onClick={handleReset}
                        disabled={resetLoading}
                      >
                        {resetLoading ? 'Resetting...' : 'Confirm Reset'}
                      </Button>
                    </Box>
                  }
                >
                  <Typography variant="body2">
                    <strong>Are you sure?</strong> This will permanently delete:
                  </Typography>
                  <Typography variant="body2" component="ul" sx={{ mt: 1, mb: 0, pl: 2 }}>
                    <li>All positions (open, pending, and closed)</li>
                    <li>All transaction history</li>
                    <li>All recommendations</li>
                  </Typography>
                </Alert>
              )}
            </>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={loading || !name.trim() || !maxPositions}
        >
          Save Changes
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditPortfolioDialog;